import { Flex } from "@chakra-ui/react";
import type { Icons } from "components/icon/iconTypes";
import type { Data, Time } from "convert";
import { convert, getMeasureKind } from "convert";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { isNil, sortBy, uniqBy } from "es-toolkit/compat";
import type {
  Maybe,
  ResourceMetrics,
  Service,
  ServiceConfigHistory,
} from "graphql/generated";
import { Type } from "graphql/generated";
import Cookies from "js-cookie";
import React from "react";
import type { ServiceType } from "types";
import { ServiceTypeProductId } from "types";
import type { ValueOf } from "types/extensions";
import type { ThemeColors } from "../theme/tokens/colors";
// @ts-ignore
import sqlFormatter from "sql-format";
import { FREE_SERVICE_STORAGE_CAP_MIB } from "stores/constants";
import type { Configuration } from "stores/productStore";
import { colors } from "theme-v3/tokens/colors";

dayjs.extend(utc);

/* ReactLazyPreload */
export interface LazyPreload<Props> extends React.LazyExoticComponent<
  React.ComponentType<Props>
> {
  preload: () => {};
}

export function ReactLazyPreload<Props>(
  importStatement: () => Promise<{ default: React.ComponentType<Props> }>,
) {
  // use Object.assign to set preload
  // otherwise it will complain that Component doesn't have preload
  const Component: LazyPreload<Props> = Object.assign(
    React.lazy(importStatement),
    {
      preload: importStatement,
    },
  );

  return Component;
}

export const findById = ({ items, id }: { items: any[]; id: string }) =>
  items.find((item) => item?.id === id);

export const excludeById = ({ items, id }: { items: any[]; id: string }) =>
  sortBy(
    [...items].filter(({ id: itemId }) => id !== itemId),
    ["id"],
  );

export const diskPercentageNumber = (usedDisk: number, disk: number): number =>
  usedDisk && disk ? +((usedDisk / disk) * 100).toFixed(0) : 0;

export const getProgressColor = (
  value: number,
): { foreground: ThemeColors; background: ThemeColors } => {
  return value >= 99
    ? { foreground: "error.fg", background: "error.default" }
    : value >= 75
      ? { foreground: "warning.fg", background: "warning.default" }
      : { foreground: "success.fg", background: "success.default" };
};

export const getResources = ({
  resources,
  isFixed,
}: any): Record<"cpu" | "ram" | "disk" | "replicaCount", number> => {
  const { cpu, ram, disk, replicaCount } = (() => {
    if (resources?.length > 0) {
      return {
        cpu: (resources?.[0]?.spec.milliCPU || 0) / 1000,
        ram: resources?.[0]?.spec.memoryGB,
        disk: isFixed
          ? FREE_SERVICE_STORAGE_CAP_MIB / 1024
          : resources?.[0]?.spec.storageGB || 0,
        replicaCount: resources?.[0]?.spec.replicaCount,
      };
    } else {
      return { cpu: 0, ram: 0, disk: 0, replicaCount: 0 };
    }
  })();

  return { cpu, ram, disk, replicaCount };
};

export const getResourceUsage = ({
  metrics,
  isFixed,
}: {
  metrics?: Maybe<ResourceMetrics>;
  isFixed?: boolean;
}) => {
  const usedCPU = metrics ? (metrics.milliCPU ? metrics.milliCPU : 0) : 0;
  const usedRAM = metrics ? Number((metrics.memoryMB! / 1024).toFixed(2)) : 0;
  const usedDisk = metrics
    ? (isFixed
        ? Math.min(metrics.storageMB!, FREE_SERVICE_STORAGE_CAP_MIB)
        : metrics.storageMB!) / 1024
    : 0;
  return { usedCPU, usedRAM, usedDisk };
};

type Unit = Data | Time;

interface ConvertUnit {
  value: number;
  from: Unit;
  to: Unit;
  precision?: number;
}

interface ConvertUnitToBest {
  value: number;
  from: Unit;
  precision?: number;
  kind?: "metric" | "imperial";
}

export interface ConvertUnitReturn {
  unit: string;
  value: number;
  valueWithUnit: string;
}

/**
 A wrapper around the `convert` library to make
 our unit conversions smoother and more consistent.

 The function returns an object that contains either the
 value as a number, or a string containing both the value and
 the unit (e.g. `4.32 GB`)

 Notes:
 - The `precision` arg handles the input for the `.toFixed()`
 method
 - Using `parseFloat()` removes trailing zeroes from a number

 */

export function convertUnit(param: ConvertUnitToBest): ConvertUnitReturn;
export function convertUnit(param: ConvertUnit): ConvertUnitReturn;
export function convertUnit(param: any): ConvertUnitReturn {
  const fallback: ConvertUnitReturn = {
    unit: "",
    value: NaN,
    valueWithUnit: "-",
  };

  if (!param || typeof param !== "object") {
    return fallback;
  }

  const {
    value,
    from,
    to,
    precision = 2,
    kind = "imperial",
  } = param as {
    value?: number;
    from?: Unit;
    to?: Unit;
    precision?: number;
    kind?: "metric" | "imperial";
  };

  const safePrecision = Number.isFinite(precision)
    ? Math.min(20, Math.max(0, Math.trunc(precision)))
    : 2;
  const safeKind = kind === "metric" ? "metric" : "imperial";
  const fallbackUnit = typeof to === "string" ? to : from || "";

  if (!Number.isFinite(value) || !from) {
    return {
      unit: fallbackUnit,
      value: NaN,
      valueWithUnit: fallbackUnit ? `- ${fallbackUnit}` : "-",
    };
  }

  const safeValue = value as number;

  try {
    if (to) {
      const conversion = convert(safeValue, from).to(to);
      const convertedValue = Number(conversion);

      if (!Number.isFinite(convertedValue)) {
        return {
          unit: to,
          value: NaN,
          valueWithUnit: `- ${to}`,
        };
      }

      const formattedVal = parseFloat(convertedValue.toFixed(safePrecision));
      return {
        unit: to,
        value: formattedVal,
        valueWithUnit: `${formattedVal} ${to}`,
      };
    }

    const conversion = convert(safeValue, from).to("best", safeKind);
    const quantity =
      typeof conversion.quantity === "bigint"
        ? NaN
        : Number(conversion.quantity);
    const formattedVal = Number.isFinite(quantity)
      ? parseFloat(quantity.toFixed(safePrecision))
      : NaN;

    if (
      Number.isFinite(formattedVal) &&
      getMeasureKind(conversion.unit) === 2 &&
      formattedVal === 0
    ) {
      return {
        unit: "Bytes",
        value: 0,
        valueWithUnit: "0 B",
      };
    }

    // MeasureKind.Time === 10. When value is 0, the library picks "fs" (femtoseconds) as best.
    if (
      Number.isFinite(formattedVal) &&
      getMeasureKind(conversion.unit) === 10 &&
      formattedVal === 0
    ) {
      return {
        unit: "s",
        value: 0,
        valueWithUnit: "0 s",
      };
    }

    return {
      unit: conversion.unit,
      value: formattedVal,
      valueWithUnit: Number.isFinite(formattedVal)
        ? `${formattedVal} ${conversion.unit}`
        : `- ${conversion.unit}`,
    };
  } catch {
    return {
      unit: fallbackUnit,
      value: NaN,
      valueWithUnit: fallbackUnit ? `- ${fallbackUnit}` : "-",
    };
  }
}

type MetricApprox = {
  num: number;
  base?: number;
  units?: string[] | string;
  decimalPlaces?: number;
};

//gives value in metric prefixes: kilo, mega, giga, tera, peta
const metricApproxBase = ({
  num,
  base = 1000,
  units = " KMGTP",
  decimalPlaces = 1,
}: MetricApprox) => {
  if (num <= 0) {
    return { number: 0, units: "" };
  }
  if (num < 1) {
    return { number: num, units: units[0] };
  }
  const power = Math.min(
    units.length - 1,
    Math.floor(Math.log(num) / Math.log(base)),
  );
  const numero = Number((num / base ** power).toFixed(decimalPlaces));
  return {
    number: numero,
    units: units[power],
  };
};

export const metricApprox = ({ num, ...props }: MetricApprox) => {
  if (num <= 0) {
    return "0";
  }
  const { number: numero, units } = metricApproxBase({ num, ...props });
  return `${numero} ${units}`;
};

/**
 * Controls whether the "need more compute" recommendation
 * should be shown. You can either pass in a service object,
 * or an object with the milliCPU and storageGB properties you
 * want to compare.
 *
 * @param service - a Service object
 * @param config - a config object
 * @property config.milliCPU - the milliCPU value to compare
 * @property config.storageGB - the storageGB value to compare
 */
export const moreComputeSuggested = (
  service: Service | {} = {},
  { milliCPU, storageGB }: { milliCPU?: number; storageGB?: number } = {},
) => {
  /**
   * If neither milliCPU nor storageGB is provided,
   * and the serviceInfo object has something in it, then we
   * use the actual resource usage from that service to trigger
   * the recommendation. (e.g. in the OverviewPage, and
   * serviceCardComponents).
   *
   * But if milliCPU and storageGB is provided, then we use
   * those values to trigger the recommendation.
   */

  let cpu = milliCPU;
  let storage = storageGB;

  if (
    (isNil(milliCPU) || isNil(storageGB)) &&
    service &&
    Object.keys(service).length > 0
  ) {
    const { metrics } = service as Service;
    let { usedDisk } = getResourceUsage({ metrics });
    storage = usedDisk;
  }

  if (cpu && storage) {
    if (cpu <= 500 && storage >= 50) {
      return true;
    }
    if (cpu <= 1000 && storage >= 600) {
      return true;
    }
  }
  return false;
};

export const getCreated = (created?: string) => {
  if (!created) {
    return null;
  }
  dayjs.extend(relativeTime);
  const day = dayjs(created).fromNow();
  return `${day.replace(/\\D/g, "")}`;
};

export const getLastServiceUpdateEvent = (
  serviceConfigHistory: ServiceConfigHistory,
) => {
  const { compute = [], storage = [] } = serviceConfigHistory;

  const updateEvents = [...compute.slice(0, -1), ...storage.slice(0, -1)];
  const lastUpdateEvent = updateEvents.sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
  )[0];

  return {
    text:
      lastUpdateEvent && "storageGB" in lastUpdateEvent
        ? "Storage configuration resize."
        : "Compute configuration resize.",
    created: lastUpdateEvent?.created,
  };
};

// Accepts a service and returns a standized format for replica data.
export const replicaInfo = (service: Service | undefined) => {
  const replicaOrdinals = service?.replicaOrdinals;
  const replicaStatus = service?.replicaStatus;
  const replicaCount = service?.resources?.[0]?.spec?.replicaCount;

  if (service?.type === Type.Grafana) {
    return {
      replicaOrdinals: undefined,
      replicaStatus: undefined,
      replicaCount: undefined,
    };
  }

  return {
    replicaOrdinals,
    replicaStatus,
    replicaCount,
  };
};

export type ComputeOption = {
  value: { milliCPU: number; memoryGB: number };
  id: string;
  regionCode: string;
  label: string;
  iconRight?: Icons;
};

export const buildComputeDropdownOptions = (
  serviceType: string,
  configuration: Configuration[], //from product store
  regionCode: string,
) => {
  let computeDropdownOptions = configuration.find((config) => config.productId)
    ? configuration.reduce(
        (
          filteredByServiceRegion: ComputeOption[],
          {
            cpu,
            value,
            memory,
            label,
            label2,
            regionCode: configRegionCode,
            productId: configuredProductId,
          },
        ) => {
          if (
            configRegionCode === regionCode &&
            configuredProductId ===
              ServiceTypeProductId[serviceType as ValueOf<typeof ServiceType>]
                .Config
          ) {
            return [
              ...filteredByServiceRegion,
              {
                value: { milliCPU: cpu || 0, memoryGB: memory || 0 },
                id: `${value}`,
                regionCode,
                label: `${label} / ${label2}`,
                productId: configuredProductId,
              },
            ];
          }
          return filteredByServiceRegion;
        },
        [],
      )
    : configuration.reduce(
        (
          filteredByServiceRegion: ComputeOption[],
          { cpu, value, memory, label, label2, regionCode: configRegionCode },
        ) => {
          if (configRegionCode === regionCode) {
            return [
              ...filteredByServiceRegion,
              {
                value: { milliCPU: cpu || 0, memoryGB: memory || 0 },
                id: `${value}`,
                regionCode,
                label: `${label} / ${label2}`,
              },
            ];
          }
          return filteredByServiceRegion;
        },
        [],
      );

  computeDropdownOptions = uniqBy(computeDropdownOptions, "id");
  return computeDropdownOptions;
};

export const getLatestVersion = (versions: string[] = []) =>
  versions
    .slice()
    .sort((a: string, b: string) =>
      b.localeCompare(a, undefined, { numeric: true, sensitivity: "base" }),
    )?.[0];

export const getGaClientId = () => {
  const gaCookie = Cookies.get("_ga");

  if (gaCookie) {
    return gaCookie.split(".").slice(2).join(".");
  }

  return null;
};

// gtag.js stores `_gcl_aw` as `GCL.<creation timestamp>.<gclid>`; gclids are
// unpadded URL-safe base64 (no dots, no `=`), so anything else — including a
// same-origin ad integration overwriting the cookie with its own value —
// isn't a value we should trust.
const GCL_AW_VALUE_PATTERN = /^GCL\.\d+\.([\w-]+)$/;

export const extractGclid = (cookieValue: string) =>
  cookieValue.match(GCL_AW_VALUE_PATTERN)?.[1] ?? null;

/**
 * `_gcl_aw` is a first-party cookie set by Google Ads' conversion linker
 * (via gtag.js) when a visitor arrives from a Google Ads click. It carries
 * the click id (gclid) so a later conversion — e.g. a signup — can be
 * attributed back to that ad click.
 */
export const getGclAwCookie = () => {
  const gclAwCookie = Cookies.get("_gcl_aw");

  if (gclAwCookie) {
    return extractGclid(gclAwCookie);
  }

  return null;
};

export const getHubspotUtk = () => {
  const cookie = Cookies.get("hubspotutk");

  if (cookie) {
    return cookie;
  } else {
    return null;
  }
};

// Mirrors the marketing site's (timescale-website) `signup_success` GTM
// event for parity, but only for signups completed via Google SSO
// (IDPLanding) — native signups already fire their own `gtm.formSubmit`
// event on form submission (see SignUpPage).
export const sendGoogleSignupSuccessEvent = ({ email }: { email: string }) => {
  try {
    if (typeof window === "undefined" || !window.dataLayer) {
      return;
    }
    const emailDomain = email.split("@")[1] ?? "";
    window.dataLayer.push({
      event: "signup_success",
      user_properties: {
        email_domain: { value: emailDomain },
      },
      method: "google",
    });
  } catch (error) {
    console.error(`There was an error sending signup success event: ${error}`);
  }
};

export const getPathnameSegments = (
  pathname: string,
  { filterOut = [] }: { filterOut: unknown[] },
) => {
  return pathname
    .split("/")
    .filter((segment) => !!segment && !filterOut.includes(segment));
};

export const matchesRegexps: (value: string, regexps: RegExp[]) => boolean = (
  string,
  regexps,
) => {
  return regexps.some((reg) => reg.test(string));
};

const genericDay = dayjs();

export interface WindowOption {
  value: number;
  label: string;
}

export const gmtOffsets: WindowOption[] = [
  ...Array.from(Array(15 - -12).keys()).map((i) => i - 12),
  5.5, // India Standard Time (GMT +05:30)
]
  .sort((a, b) => a - b)
  .map((value) => ({
    value,
    label: genericDay.utcOffset(value)?.format("([GMT] Z)"),
  }));

export const utcOffsets: WindowOption[] = Array.from(
  Array(15 - -12).keys(),
).map((i) => ({
  value: i - 12,
  label: genericDay.utcOffset(i - 12)?.format("([UTC] Z)"),
}));

export const formatSql = (queryText: string, language: string = "pl/sql") => {
  const formatted = sqlFormatter.format(queryText, { language: language });
  return (
    formatted
      // The sql-format library incorrectly adds spaces to SQL arrows
      // so here we'll remove them
      .replace(/<\s*=\s*>/g, "<=>")
      .replace(/=\s+>/g, "=>")
  );
};

export const escapeSingleQuotes = (str: string) => str.replace(/'/g, "''");

export const generateRandomName = (prefix = "") =>
  `${prefix}-${Math.floor(10000 + Math.random() * 90000)}`;

export const makeRandomDBName = (prefix = "db") => {
  return generateRandomName(prefix);
};

export const swapFaviconForEnv = () => {
  const favicon = document.querySelector<HTMLLinkElement>(
    'link[rel="shortcut icon"]',
  );
  if (!favicon) return;
  const { href } = window.location;
  if (/local/.test(href)) favicon.href = "/favicon-yellow.png";
  else if (/dev/.test(href)) favicon.href = "/favicon-green.png";
  else if (/onrender/.test(href)) favicon.href = "/favicon-purple.png";
};

export const printSha = () => {
  if (import.meta.env.PROD) {
    const sha = (import.meta.env.VITE_SHA || "").substring(0, 7);
    const version = import.meta.env.VITE_VERSION
      ? `${import.meta.env.VITE_VERSION} `
      : "";
    console.info(
      `%c🐯 Tiger Cloud%c  %c${version}#${sha}`,
      `background: ${colors.black.value}; border-radius:0.5em; padding:0.2em 0.5em; color: ${colors.electricYellow[400].value};`,
      "",
      `background: ${colors.green[600].value}; border-radius:0.5em; padding: 0.2em 0.5em 0.1em 0.5em; color: ${colors.black.value}; font-weight: 700;`,
    );
  }
};

export const buildIdFromLabel = (label: string): string =>
  label.replace(/ /g, "_").toLowerCase();

export const convertNumberOfNodes = (number: number) => {
  return number + 1;
};

/**
 * Format trial days left message
 */
export const formatTrialMessage = (daysLeft: number, remainingUSD?: string) => {
  return (
    <Flex color="highlight.fg" data-cy="trial-days-display">
      Trial:&nbsp;
      {daysLeft} day<span>{daysLeft !== 1 ? "s" : ""}</span>
      {remainingUSD && <Flex>&nbsp;&bull;&nbsp; ${remainingUSD} left</Flex>}
    </Flex>
  );
};

export const plural = (count: number, singular: string, pluralForm: string) =>
  count === 1 ? singular : pluralForm;
